import { Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { LiabilityForm } from '@/modules/liabilities/LiabilityModal/LiabilityForm';
import { getLiabilityIntegrationPrefilledFields } from '@/modules/liabilities/LiabilityModal/LiabilityForm.integration';
import { LiabilityFormShape } from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import { OVERLAY_MD } from '@/styles/tokens/panelWidths';

import {
  LiabilitiesStepFormShape,
  LiabilitiesStepRowData,
} from './LiabilitiesStep.types';

/**
 * Extracts the LiabilityFormShape fields from a LiabilitiesStepRowData.
 * The step row has extra fields (shouldImport, integrationLiabilityId) that
 * aren't part of the liability form.
 */
function toLocalFormValues(row: LiabilitiesStepRowData): LiabilityFormShape {
  return {
    name: row.name,
    issuerId: row.issuerId,
    recipientId: row.recipientId,
    outstandingBalance: row.outstandingBalance,
    balanceAsOfDate: row.balanceAsOfDate ?? new Date(),
    notes: row.notes,
    hasDifferentPaymentSource: row.hasDifferentPaymentSource,
    alternateSourceId: row.alternateSourceId,
    _showExpandedPaymentDetails: row._showExpandedPaymentDetails,
    paymentKind: row.paymentKind,
    paymentFrequency: row.paymentFrequency,
    paymentAmount: row.paymentAmount,
    interestRate: row.interestRate,
    termStartDate: row.termStartDate,
    termEndDate: row.termEndDate,
    // Pass through so the form can derive isIntegrationLinked from form state
    integrationLiabilityId: row.integrationLiabilityId,
  };
}

interface LiabilitiesStepEditDrawerProps {
  integrationLiabilityId: string;
  onClose: () => void;
}

export function LiabilitiesStepEditDrawer({
  integrationLiabilityId,
  onClose,
}: LiabilitiesStepEditDrawerProps) {
  const { setValue, getValues } = useFormContext<LiabilitiesStepFormShape>();

  const rowData = getValues(`liabilities.${integrationLiabilityId}`);
  const initialPrefilledFields = useMemo(
    () => getLiabilityIntegrationPrefilledFields(rowData),
    [rowData]
  );

  // Create a local form instance seeded from the parent step form.
  // This isolates edits so cancel is free (just discard) and save
  // copies values back to the parent.
  const localFormMethods = useForm<LiabilityFormShape>({
    defaultValues: useMemo(() => toLocalFormValues(rowData), [rowData]),
  });

  const handleSave = useCallback(() => {
    const { integrationLiabilityId: _iid, ...localValues } =
      localFormMethods.getValues();
    const currentRow = getValues(`liabilities.${integrationLiabilityId}`);

    // Merge local form values back into the parent step form,
    // preserving step-only fields (shouldImport, integrationLiabilityId).
    setValue(`liabilities.${integrationLiabilityId}`, {
      ...currentRow,
      ...localValues,
    });
    onClose();
  }, [localFormMethods, getValues, setValue, integrationLiabilityId, onClose]);

  return (
    <SideDrawer
      isOpen
      onClose={onClose}
      width={OVERLAY_MD}
      heading="Edit liability"
      body={
        <FormProvider {...localFormMethods}>
          <Stack pt={2}>
            <LiabilityForm
              isIntegrationLinked
              hideIntegrationSelect
              initialIntegrationPrefilledFields={initialPrefilledFields}
            />
          </Stack>
        </FormProvider>
      }
      footer={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave}>
            Save changes
          </Button>
        </Stack>
      }
    />
  );
}
